var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Checkbox, MessageBanner, Select, TextArea, } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { useOkapiKy } from "@folio/stripes/core";
import { AgendaChangeModal } from "./AgendaChangeModal";
import { setGlobalType, setIssueAgenda, setTitleAgenda, } from "../../api/agendaService";
import { otherAgenda, } from "../../types/agenda";
import { usePeriodicalFrequencyOptions } from "../../hooks/usePeriodicalFrequencyOptions";
import css from "./Agenda.module.css";
var otherType = function (type) {
    return type === "PERIODICAL" ? "MONOGRAPH" : "PERIODICAL";
};
export var ChangeAgendaDialog = function (_a) {
    var _b;
    var open = _a.open, mode = _a.mode, name = _a.name, titleAgenda = _a.titleAgenda, issueAgenda = _a.issueAgenda, _c = _a.wholePeriodicalFromIssue, wholePeriodicalFromIssue = _c === void 0 ? false : _c, onClose = _a.onClose, onChanged = _a.onChanged;
    var formatMessage = useIntl().formatMessage;
    var kyInstance = useOkapiKy();
    var frequencyOptions = usePeriodicalFrequencyOptions();
    var _d = useState(""), frequency = _d[0], setFrequency = _d[1];
    var _e = useState(""), note = _e[0], setNote = _e[1];
    var _f = useState(false), continuesPublication = _f[0], setContinuesPublication = _f[1];
    useEffect(function () {
        if (!open) {
            setFrequency("");
            setNote("");
            setContinuesPublication(false);
        }
    }, [open]);
    var targetAgenda = useMemo(function () {
        if (mode === "issue") {
            return issueAgenda ? otherAgenda(issueAgenda.agenda) : null;
        }
        return titleAgenda ? otherAgenda(titleAgenda.agenda) : null;
    }, [mode, issueAgenda, titleAgenda]);
    var targetType = titleAgenda
        ? otherType(titleAgenda.globalType)
        : null;
    var needsFrequency = mode !== "issue" &&
        !!titleAgenda &&
        !titleAgenda.hasPeriodicalStructure &&
        !titleAgenda.specialIssueLink &&
        (mode === "global"
            ? targetType === "PERIODICAL"
            : targetAgenda === "PERIODICALS");
    var asksContinuation = mode === "library" &&
        needsFrequency &&
        (titleAgenda === null || titleAgenda === void 0 ? void 0 : titleAgenda.globalType) === "MONOGRAPH";
    var continuesPublicationValue = asksContinuation
        ? continuesPublication
        : undefined;
    var frequencyValue = needsFrequency
        ? frequency || undefined
        : undefined;
    var noteValue = note.trim() || undefined;
    var subjectId = mode === "issue" ? issueAgenda === null || issueAgenda === void 0 ? void 0 : issueAgenda.issueId : titleAgenda === null || titleAgenda === void 0 ? void 0 : titleAgenda.titleId;
    var canSubmit = !!subjectId &&
        (mode === "global" ? !!targetType : !!targetAgenda) &&
        (!needsFrequency || !!frequencyValue);
    var run = useCallback(function (dryRun) {
        if (!subjectId) {
            return Promise.reject(new Error("The agenda change has no subject"));
        }
        if (mode === "issue" && targetAgenda) {
            return setIssueAgenda(kyInstance, subjectId, { agenda: targetAgenda, note: noteValue }, dryRun);
        }
        if (mode === "global" && targetType) {
            return setGlobalType(kyInstance, subjectId, { type: targetType, frequency: frequencyValue, note: noteValue }, dryRun);
        }
        if (mode === "library" && targetAgenda) {
            return setTitleAgenda(kyInstance, subjectId, {
                agenda: targetAgenda,
                frequency: frequencyValue,
                continuesPublication: continuesPublicationValue,
                note: noteValue,
            }, dryRun);
        }
        return Promise.reject(new Error("The agenda change has no target"));
    }, [
        mode,
        kyInstance,
        subjectId,
        targetAgenda,
        targetType,
        frequencyValue,
        continuesPublicationValue,
        noteValue,
    ]);
    var previewKey = useMemo(function () { return [
        mode,
        subjectId,
        targetAgenda,
        targetType,
        frequencyValue,
        continuesPublicationValue,
    ]; }, [
        mode,
        subjectId,
        targetAgenda,
        targetType,
        frequencyValue,
        continuesPublicationValue,
    ]);
    var headingId = mode === "global"
        ? "ui-br-legaldeposits.agenda.dialog.heading.global"
        : "ui-br-legaldeposits.agenda.dialog.heading.".concat(targetAgenda !== null && targetAgenda !== void 0 ? targetAgenda : "MONOGRAPHS");
    var introId = mode === "global"
        ? "ui-br-legaldeposits.agenda.dialog.intro.global"
        : "ui-br-legaldeposits.agenda.dialog.intro.".concat(mode, ".").concat(targetAgenda !== null && targetAgenda !== void 0 ? targetAgenda : "MONOGRAPHS");
    var frequencySelectOptions = useMemo(function () { return __spreadArray([
        {
            value: "",
            label: formatMessage({
                id: "ui-br-legaldeposits.agenda.dialog.frequency.placeholder",
            }),
        }
    ], frequencyOptions, true); }, [frequencyOptions, formatMessage]);
    var handleFrequencyChange = useCallback(function (e) { return setFrequency(e.target.value); }, []);
    var handleContinuesPublicationChange = useCallback(function (e) {
        return setContinuesPublication(e.target.checked);
    }, []);
    var handleNoteChange = useCallback(function (e) { return setNote(e.target.value); }, []);
    return (React.createElement(AgendaChangeModal, { open: open, heading: formatMessage({ id: headingId }, { name: name }), previewKey: previewKey, canSubmit: canSubmit, run: run, onClose: onClose, onChanged: onChanged, impactScope: mode === "global" ? "allLibraries" : "library" },
        React.createElement("p", null,
            React.createElement(FormattedMessage, { id: introId, values: {
                    from: (_b = titleAgenda === null || titleAgenda === void 0 ? void 0 : titleAgenda.globalType) !== null && _b !== void 0 ? _b : "MONOGRAPH",
                    to: targetType !== null && targetType !== void 0 ? targetType : "MONOGRAPH",
                } })),
        wholePeriodicalFromIssue && mode !== "issue" && (React.createElement(MessageBanner, { type: "warning", "data-test-id": "agenda-whole-periodical" },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.dialog.wholePeriodical", values: { name: name } }))),
        mode !== "global" && (React.createElement("p", { className: css.hint },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.dialog.libraryOnly" }))),
        needsFrequency && (React.createElement(React.Fragment, null,
            React.createElement(Select, { label: formatMessage({
                    id: "ui-br-legaldeposits.agenda.dialog.frequency.label",
                }), dataOptions: frequencySelectOptions, value: frequency, onChange: handleFrequencyChange, required: true, "data-test-id": "agenda-frequency" }),
            React.createElement("p", { className: css.hint },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.dialog.frequency.hint" })))),
        asksContinuation && (React.createElement(React.Fragment, null,
            React.createElement(Checkbox, { label: formatMessage({
                    id: "ui-br-legaldeposits.agenda.dialog.continuesPublication.label",
                }, { year: new Date().getFullYear() }), checked: continuesPublication, onChange: handleContinuesPublicationChange, "data-test-id": "agenda-continues-publication" }),
            React.createElement("p", { className: css.hint },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.dialog.continuesPublication.hint" })))),
        React.createElement(TextArea, { label: formatMessage({
                id: "ui-br-legaldeposits.agenda.dialog.note.label",
            }), value: note, onChange: handleNoteChange })));
};
